import { motion } from "framer-motion";

interface ProductFailureProps {
  onNext: () => void;
  productsTried: string[];
}

const failureReasons: Record<string, { label: string; reason: string; icon: string }> = {
  creams: {
    label: "Crèmes en serums",
    reason: "Bereiken alleen de bovenste huidlaag (epidermis). Ze hydrateren, maar stimuleren geen nieuw collageen in de dermis.",
    icon: "❌",
  },
  supplements: {
    label: "Supplementen",
    reason: "Worden grotendeels afgebroken tijdens de spijsvertering. Slechts een fractie bereikt je huid.",
    icon: "❌",
  },
  facials: {
    label: "Gezichtsbehandelingen",
    reason: "Geven een tijdelijk effect aan het oppervlak, maar activeren geen diepe collageenproductie.",
    icon: "❌",
  },
  devices: {
    label: "Apparaten",
    reason: "Thuisapparaten missen de kracht om door te dringen tot de dermis waar collageen wordt aangemaakt.",
    icon: "❌",
  },
  clinic: {
    label: "Kliniekbehandelingen",
    reason: "Vullen op of verlammen spieren, maar herstellen niet je eigen natuurlijke collageenproductie.",
    icon: "❌",
  },
};

const ProductFailure = ({ onNext, productsTried }: ProductFailureProps) => {
  const relevantProducts = productsTried.filter((p) => p !== "nothing");

  // If they selected "nothing", show a generic message
  if (relevantProducts.length === 0) {
    return (
      <div className="text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="insight-card mb-8"
        >
          <h3 className="text-3xl md:text-4xl font-serif font-medium text-foreground mb-4">
            Goed dat je hier bent.
          </h3>
          <p className="text-foreground/80 font-sans text-lg md:text-xl leading-relaxed max-w-md mx-auto">
            De meeste producten op de markt werken alleen aan het oppervlak. Wij laten je zien wat écht het verschil maakt.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <button onClick={onNext} className="btn-cta">
            Ga verder
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="mb-4"
      >
        <h2 className="text-2xl md:text-4xl font-serif font-medium text-foreground mb-2 leading-tight">
          Dit is waarom het niet werkte
        </h2>
        <p className="text-foreground/80 font-sans text-lg mb-5 md:mb-8">
          Je hebt het goed geprobeerd. Maar dit is wat er ontbrak:
        </p>
      </motion.div>

      <div className="space-y-3 md:space-y-4 mb-6 md:mb-10">
        {relevantProducts.map((product, i) => {
          const info = failureReasons[product];
          if (!info) return null;
          return (
            <motion.div
              key={product}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 + i * 0.1, duration: 0.3 }}
              className="insight-card text-left flex gap-4 items-start"
            >
              <span className="text-2xl flex-shrink-0 mt-1 text-destructive font-bold">{info.icon}</span>
              <div>
                <h4 className="font-serif font-medium text-foreground text-lg mb-1">
                  {info.label}
                </h4>
                <p className="text-foreground/80 font-sans text-lg leading-relaxed">
                  {info.reason}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 + relevantProducts.length * 0.1 + 0.2 }}
      >
        <p className="text-foreground/75 font-sans text-lg mb-6 max-w-sm mx-auto">
          Het probleem was niet jouw inzet. Het was de methode.
        </p>
        <button onClick={onNext} className="btn-cta">
          Ga verder
        </button>
      </motion.div>
    </div>
  );
};

export default ProductFailure;
